import {ComponentProps, FunctionComponent, useCallback, useState} from 'react';

import Button from '@/components/button';
import Dialog from '@/components/dialog';
import {BUTTON_THEME, ButtonTheme} from '@/constants/button';
import {useWebViewContentOnDialogAppear} from '@/hooks/analytics/use-web-view-content-on-dialog-appear';
import {TrackingKeyVariants} from '../tracked-atoms/types';

export type ConfirmDialogActions = {
  label: string;
  trackingKey: TrackingKeyVariants;
  testId: string;
  type: 'confirm' | 'cancel' | string;
  onAction: () => void | Promise<void>;
  theme?: ButtonTheme;
};

export type Props = Pick<ComponentProps<typeof Dialog>, 'isVisible' | 'isLoading'> & {
  loadingMode?: 'button' | 'dialog';
  title?: string;
  text: string;
  actions: ConfirmDialogActions[];
};

const ConfirmDialog: FunctionComponent<Props> = ({
  isVisible,
  isLoading: isLoadingProp,
  loadingMode = 'button',
  title,
  text,
  actions,
}) => {
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);

  const cancelAction = actions.find(action => action.type === 'cancel');

  const onCancel = useCallback(() => {
    cancelAction?.onAction();
  }, [cancelAction]);

  const handleAction = useCallback(async (action: () => void | Promise<void>) => {
    try {
      setIsLoadingInternal(true);
      await action();
    } finally {
      setIsLoadingInternal(false);
    }
  }, []);

  const isLoading = isLoadingProp ?? isLoadingInternal;

  useWebViewContentOnDialogAppear('modale-confirmation', null, isVisible);

  return (
    <Dialog isVisible={isVisible} isLoading={loadingMode === 'dialog' && isLoading} onClose={onCancel}>
      {title && <Dialog.Header title={title} onClose={onCancel} />}

      <Dialog.Content data-testid="confirm-dialog-content">
        <div>{text}</div>

        <div className="flex justify-end gap-3 pt-8">
          {actions.map(action => (
            <Button
              key={action.testId}
              disabled={loadingMode === 'button' && isLoading}
              isLoading={action.type === 'confirm' && loadingMode === 'button' && isLoading}
              theme={action.theme ?? BUTTON_THEME.PRIMARY}
              label={action.label}
              trackingKey={action.trackingKey}
              onClick={() => handleAction(action.onAction)}
              data-testid={action.testId}
            />
          ))}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default ConfirmDialog;
